import React, { useMemo, useState } from 'react';
import { View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import BasicModal from '../../../components/BasicModal';
import moment from 'moment';
import { translate } from '../../../services/translate';
import FormInput from '../../../components/FormInput';
import FilterDatePicker from '../../../components/Filter/components/FilterDateInput';
import MyAppText from '../../../components/MyAppText';
import Toast from 'react-native-toast-message';
import { ClientError } from '../../../services/central-api/base-service';
import { cameraService } from '../../../services/central-api/cameras';
import inputStylesheet from '../../../styles/inputStyles';

export interface VideoAnalysisValues {
    prompt: string;
    msStart: number;
    msFinish: number;
}

interface Props {
    cameraId?: string;
    isModalVisible: boolean;
    setModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
}

interface AnalysisFormValues {
    prompt: string;
    startAtDate: Date | null;
    startAtHour: string;
    endAtDate: Date | null;
    endAtHour: string;
}

function NewAnalysisModal({ cameraId, isModalVisible, setModalVisible }: Props) {
    const { styles } = useStyles(stylesheet);
    const { styles: defaultInputStyles } = useStyles(inputStylesheet);
    const [formValues, setFormValues] = useState<AnalysisFormValues>({
        prompt: '',
        startAtDate: moment().toDate(),
        startAtHour: moment().subtract(5, 'm').format('HH:mm'),
        endAtDate: moment().toDate(),
        endAtHour: moment().format('HH:mm'),
    });

    const disabled = useMemo(() => {
        return Object.values(formValues).some(value => !value || value === '');
    }, [formValues]);

    async function createVideoAnalysis(): Promise<void | false> {
        try {
            if (!cameraId) {
                return false;
            }

            if (!formValues.startAtDate || !formValues.endAtDate) {
                return false;
            }

            const formatDateTime = (date: Date, time: string) =>
                moment(`${moment(date).format('DD/MM/YYYY')} ${time}`, 'DD/MM/YYYY HH:mm').valueOf();

            await cameraService.createVideoAnalysis(cameraId, {
                prompt: formValues.prompt,
                msStart: formatDateTime(formValues.startAtDate, formValues.startAtHour),
                msFinish: formatDateTime(formValues.endAtDate, formValues.endAtHour),
            });

            setModalVisible(false);
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('requestAnalysisCreated'),
            });
        } catch (err) {
            if (err instanceof ClientError) {
                Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
                return false;
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
            return false;
        }
    }

    return (
        <BasicModal
            isModalVisible={isModalVisible}
            setModalVisible={setModalVisible}
            title={translate('requestAnalysis')}
            width='medium'
            mobileHeight='fit'
            cancelButton={{
                onClick: () => {
                    setModalVisible(false);
                    setFormValues({
                        prompt: '',
                        startAtDate: moment().toDate(),
                        startAtHour: moment().format('HH:mm'),
                        endAtDate: moment().toDate(),
                        endAtHour: moment().format('HH:mm'),
                    });
                }
            }}
            primaryButton={{
                text: translate('toRequest'),
                onClick: createVideoAnalysis,
                disabled,
            }}
            disableScrollView={true}
        >
            <FormInput
                label={translate('request')}
                placeholder={translate('requestFieldPlaceholder')}
                value={formValues.prompt}
                onChangeText={(prompt) => {
                    setFormValues(old => ({
                        ...old,
                        prompt
                    }));
                }}
                invalid={(value: string) => {
                    return value == '';
                }}
                multiline
                numberOfLines={4}
            />

            <MyAppText style={defaultInputStyles.label}>{translate('analysisPeriod')}</MyAppText>

            <View style={styles.dateFieldsContainer}>
                <FilterDatePicker
                    label={translate('beginIn')}
                    date={formValues.startAtDate}
                    onChangeDate={(value) => {
                        setFormValues(old => ({
                            ...old,
                            startAtDate: typeof value === 'function' ? value(old.startAtDate) : value
                        }));
                    }}
                    time={formValues.startAtHour}
                    onChangeTime={(startAtHour) => {
                        setFormValues(old => ({
                            ...old,
                            startAtHour
                        }));
                    }}
                    zIndex={16}
                />
                <FilterDatePicker
                    label={translate('endIn')}
                    date={formValues.endAtDate}
                    onChangeDate={(value) => {
                        setFormValues(old => ({
                            ...old,
                            endAtDate: typeof value === 'function' ? value(old.endAtDate) : value
                        }));
                    }}
                    time={formValues.endAtHour}
                    onChangeTime={(endAtHour) => {
                        setFormValues(old => ({
                            ...old,
                            endAtHour
                        }));
                    }}
                    zIndex={15}
                />
            </View>

        </BasicModal>
    );
}

const stylesheet = createStyleSheet(() => ({
    dateFieldsContainer: {
        flexDirection: 'row',
        gap: {
            xs: 10,
            md: 50
        },
        flexWrap: 'wrap'
    }
}));

export default NewAnalysisModal;
